import {
  ChangeEventHandler,
  FC,
  FocusEventHandler,
  InputHTMLAttributes,
  SVGProps,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import styled, { AnyStyledComponent } from 'styled-components'
import Select from 'react-select'
import ArrowDownIcon from 'src/assets/icons/arrow-down.svg'
import Text from './Text'
import { useMediaQuery } from 'src/utilities/hooks'
import MenuCloseIcon from 'src/assets/icons/menu-close.svg?react'
import { DEFAULT_FONT_FAMILY, useTheme } from 'src/utilities/theme'
import { CountryCode } from 'libphonenumber-js'
import { countryData } from 'src/utilities/constants'

export interface CardPhoneInputRef {
  clearInputValue: () => void
}

interface CardPhoneInputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: boolean
  inputRequired?: boolean
  showRemoveButton?: boolean
  hideShadow?: boolean
  onCountryChange: (countryCode: CountryCode) => void
  selectedCountry: CountryCode
  Icon?: FC<SVGProps<SVGSVGElement> & { title?: string | undefined }>
  onInputChange?: (value: string) => void
  onInputBlur?: (value: string) => void
  onRemoveClick?: () => void
  forceDesktop?: boolean
}

interface LabelProps {
  error?: boolean
}

interface CardProps {
  hideShadow?: boolean
}

export const countryOptions = Object.entries(countryData).map(([code, data]) => ({
  value: code,
  label: `${data.name} (${data.code})`,
  flag: `https://flagcdn.com/w40/${code.toLowerCase()}.png`,
  code: data.code,
}))

const MainContainer = styled.div`
  @media ${({ theme }) => theme.queries.mobile} {
    display: grid;
    row-gap: 1.125rem;
  }
`

const CardContainer = styled.div<CardProps>`
  background-color: ${({ theme }) => theme.colors.baseItemColor};
  box-shadow: ${({ hideShadow }) => (!hideShadow ? '0px 0px 14px rgba(0, 0, 0, 0.043679)' : 'none')};
  border-radius: 0.563rem;
  overflow: hidden;
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  padding: 1rem 0.75rem 1rem 0;
`

const CardContentContainer = styled.div`
  display: grid;
  row-gap: 0.5rem;
`

const IconContainer = styled.div`
  width: 5rem;
  display: grid;
  justify-content: center;
  @media ${({ theme }) => theme.queries.mobile} {
    width: 3.813rem;
  }
`

const Input = styled.input`
  height: 2.063rem;
  padding: 0 0.75rem;
  border: 1px solid rgba(60, 60, 67, 0.29);
  border-left: 0.5px solid rgba(60, 60, 67, 0.29);
  border-radius: 0 0.563rem 0.563rem 0;
  box-sizing: border-box;
  outline: none;
  color: ${({ theme }) => theme.colors.baseItemTextColor};
  background-color: ${({ theme }) => theme.colors.fifthItemColor};
  ${({ theme }) => ({ ...theme.typography.placeholder })};
  ::placeholder {
    color: ${({ theme }) => theme.colors.placeholderTextColor};
    opacity: 0.7;
  }
`

const InputContainer = styled.div`
  height: 2.063rem;
  display: grid;
  grid-template-columns: 5rem minmax(0, 1fr);
`

const PrefixContainer = styled.div`
  height: 2.063rem;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  border: 1px solid rgba(60, 60, 67, 0.29);
  border-right: 0.5px solid rgba(60, 60, 67, 0.29);
  border-radius: 0.563rem 0 0 0.563rem;
  background-color: ${({ theme }) => theme.colors.fifthItemColor};
`

const PrefixContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  width: 100%;
  padding: 0 0.5rem;
  position: relative;
`

const DropdownArrow = styled.img<{ open?: boolean }>`
  width: 1rem;
  height: 1rem;
  object-fit: contain;
  position: absolute;
  right: 0;
  margin-right: 0.5rem;
  ${({ open }) => open && `transform: rotate(180deg);`}
`

const RemoveButtonContainer = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  padding: 0 0.75rem;
  display: flex;
  align-items: center;
`

const RemoveButton = styled.button.attrs({ type: 'button' })`
  border: none;
  padding: 0;
  cursor: pointer;
  background-color: transparent;
  display: block;
  width: 1rem;
  height: 1rem;
`

const RemoveButtonImage = styled(MenuCloseIcon as unknown as AnyStyledComponent)`
  width: 100%;
  height: 100%;
  color: ${({ theme }) => theme.colors.primaryTextColor};
`

const Label = styled(Text as unknown as AnyStyledComponent).attrs<LabelProps>(({ error }) => ({
  color: error ? 'errorTextColor' : 'baseItemTextColor',
}))<LabelProps>``

const CardPhoneInput = forwardRef<CardPhoneInputRef, CardPhoneInputProps>(({ value, label, error, inputRequired, showRemoveButton, Icon, onInputChange, onInputBlur, onRemoveClick, forceDesktop, hideShadow, onCountryChange, selectedCountry, ...props }, ref) => {
  const [inputValue, setInputValue] = useState<string>('')
  const [tempHideDialCode, setTempHideDialCode] = useState(false)
  const inputElementRef = useRef<HTMLInputElement | null>(null)
  const selectRef = useRef<any>(null)
  const { isMobile } = forceDesktop === true ? { isMobile: false } : useMediaQuery()
  const formattedLabel = useMemo(() => inputRequired ? `${label}*` : label, [label, inputRequired])
  const { theme } = useTheme()
  const [open, setOpen] = useState<boolean>(false)

  const clearInputValue = () => setInputValue('')

  const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const val = e.target.value
    setInputValue(val)
    onInputChange?.(val)
  }

  const handleBlur: FocusEventHandler<HTMLInputElement> = (e) => {
    onInputBlur?.(e.target.value)
  }

  useEffect(() => {
    if (value) setInputValue(String(value))
  }, [value])

  useImperativeHandle(ref, () => ({
    clearInputValue,
    inputElement: inputElementRef.current,
  }))

  const formatOptionLabel = (option: any, { context }: any) => {
    if (context === 'value') {
      return <span>{tempHideDialCode ? '' : option.code}</span>
    }
  
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem' }}>
        <img src={option.flag} alt={option.label} style={{ width: 20 }} />
        <span style={{ whiteSpace: 'nowrap' }}>{option.label}</span>
      </div>
    )
  }

  return (
    <MainContainer>
      {isMobile && !!label && (
        <Label type="productPageOptionName" error={error}>{formattedLabel}</Label>
      )}
      <CardContainer hideShadow={hideShadow}>
        {!!Icon && (
          <IconContainer>
            <Icon width="auto" height="1.813rem" color={theme.colors.baseItemTextColor} />
          </IconContainer>
        )}
        <CardContentContainer>
          {!isMobile && label && (
            <Label type="checkoutTitle" error={error}>{formattedLabel}</Label>
          )}
          <InputContainer>
            <PrefixContainer>
              <PrefixContent onClick={() => selectRef.current?.focus()}>
                <Select
                  ref={selectRef}
                  options={countryOptions}
                  onMenuOpen={() => {
                    setTempHideDialCode(true)
                    setOpen(true)
                  }}
                  onMenuClose={() => {
                    setTempHideDialCode(false)
                    setOpen(false)
                  }}
                  value={countryOptions.find((c) => c.value === selectedCountry)}
                  onChange={(opt) => {
                    onCountryChange(opt!.value as CountryCode)
                  }}
                  isSearchable={false}
                  menuPortalTarget={document.body}
                  formatOptionLabel={formatOptionLabel}
                  components={{ IndicatorSeparator: () => null }}
                  styles={{
                    control: base => ({
                      ...base,
                      height: '100%',
                      width: '100%', 
                      minHeight: 'unset',
                      border: 'none',
                      backgroundColor: 'transparent',
                      boxShadow: 'none',
                      boxSizing: 'border-box'
                    }),
                    valueContainer: base => ({
                      ...base,
                      padding: '0 0.15rem',
                      justifyContent: 'flex-start',
                      fontFamily: DEFAULT_FONT_FAMILY,
                    }),
                    singleValue: base => ({
                      ...base,
                      fontSize: '1rem',
                      fontFamily: DEFAULT_FONT_FAMILY,
                      color: 'inherit',
                      margin: '0',
                    }),
                    option: (base, state) => ({
                      ...base,
                      padding: '0.75rem 1rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      backgroundColor: state.isFocused ? '#f0f0f0' : '#fff',
                      color: '#000',
                    }),
                    indicatorsContainer: () => ({ display: 'none' }),
                    indicatorSeparator: () => ({ display: 'none' }),
                    menuPortal: base => ({ ...base, zIndex: 9999 }),
                    menu: base => ({ ...base, width: '16rem' }),
                  }}
                />
                <DropdownArrow src={ArrowDownIcon} alt="toggle" open={open} />
              </PrefixContent>
            </PrefixContainer>
            <Input ref={inputElementRef} type={'number'} value={inputValue} onBlur={handleBlur} onChange={handleChange} {...props} />
            {!!showRemoveButton && (
              <RemoveButtonContainer>
                <RemoveButton onClick={onRemoveClick}><RemoveButtonImage /></RemoveButton>
              </RemoveButtonContainer>
            )}
          </InputContainer>
        </CardContentContainer>
      </CardContainer>
    </MainContainer>
  )
})

export default CardPhoneInput
